// Framework-agnostic driver for the standalone MyCorr graph widget
// (`embed_graph` — graph_engine only, ~125 KB gzip). Mounts `GraphEmbed` into a
// <canvas>, loads a static model, forwards pointer/wheel input, keeps the
// canvas sized at the right DPR, honours a theme, and frees the WASM on
// teardown. Zero dependencies. Ship next to `mycorr_graph.js` +
// `mycorr_graph_bg.wasm`.
//
//   import { mountGraph } from "./graph-embed.js";
//   const h = await mountGraph({ canvas, model, theme: "dark" });
//   // later: h.destroy();

import init, { GraphEmbed } from "./mycorr_graph.js";

let wasmReady = null;

// wasm-bindgen wraps `GraphEmbed` in a WasmRefCell: every `&mut self` method
// borrows on entry and releases on return. If a second method is entered while
// the first is still on the stack — e.g. an app call synchronously dispatches a
// `resize` / DOM event whose listener calls back in — the second borrow throws
// "recursive use of an object detected which would lead to unsafe aliasing in
// rust", and (because the exception unwinds without releasing the borrow) every
// later call throws too. JS is single-threaded, so serializing is enough: run
// one call at a time and defer any that arrives mid-flight to a microtask, so it
// lands after the outer call unwinds and the borrow is free. All entry points
// (pointer/resize/rAF listeners AND the returned handle) share this one guarded
// instance, so nothing can re-enter. Getters (`selected_id`, `pressed_badge`,
// `animate`) are only ever called at the top of the stack, never re-entrantly,
// so deferral only affects fire-and-forget mutations whose return value is
// unused.
function guardReentrancy(app) {
  let inCall = false;
  const deferred = [];
  const flush = () => {
    while (deferred.length) deferred.shift()();
  };
  return new Proxy(app, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== "function") return value;
      return (...args) => {
        if (inCall) {
          deferred.push(() => value.apply(target, args));
          return undefined;
        }
        inCall = true;
        try {
          return value.apply(target, args);
        } finally {
          inCall = false;
          if (deferred.length) queueMicrotask(flush);
        }
      };
    },
  });
}

export async function mountGraph({ canvas, model, theme, sizeTarget, wasmUrl, onSelect }) {
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error("mountGraph: `canvas` must be an HTMLCanvasElement");
  }
  if (!canvas.id) canvas.id = `mycorr-graph-${Math.random().toString(36).slice(2)}`;

  if (!wasmReady) wasmReady = init(wasmUrl);
  await wasmReady;

  const box = sizeTarget ?? canvas.parentElement ?? canvas;
  const modelJson = typeof model === "string" ? model : JSON.stringify(model);

  const app = guardReentrancy(new GraphEmbed(canvas.id));

  const applySize = () => {
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round(box.clientWidth || canvas.clientWidth));
    const h = Math.max(1, Math.round(box.clientHeight || canvas.clientHeight));
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    app.set_size(w, h, dpr); // sizes the backing store + fits + renders
  };

  applySize();
  if (theme) app.set_theme(theme);
  app.load(modelJson);

  // --- render loop: runs while the layout is moving, restarts on input ---
  let rafId = null;
  let destroyed = false;
  const frame = (ts) => {
    if (destroyed) return;
    rafId = app.animate(ts) ? requestAnimationFrame(frame) : null;
  };
  const ensureFrame = () => {
    if (!destroyed && rafId === null) rafId = requestAnimationFrame(frame);
  };
  ensureFrame();

  // --- input (CSS px relative to the canvas) ---
  const xy = (e) => {
    const r = canvas.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  let lastSel = null;
  const emitSelect = () => {
    const sel = app.selected_id() ?? null;
    if (sel !== lastSel) {
      lastSel = sel;
      onSelect?.(sel);
    }
  };
  const onDown = (e) => {
    const [x, y] = xy(e);
    app.pointer_down(x, y);
    ensureFrame();
  };
  const onMove = (e) => { const [x, y] = xy(e); app.pointer_move(x, y); };
  // The engine commits a selection on release, so only a click (never a drag) selects.
  const onUp = (e) => {
    const [x, y] = xy(e);
    app.pointer_up(x, y);
    emitSelect();
    ensureFrame();
  };
  const onOut = () => app.pointer_out();
  // Wheel deltas are device- and browser-dependent: Chrome reports pixels
  // (deltaMode 0), Firefox often reports LINES (deltaMode 1, deltaY ≈ 3), and
  // paging mice report pages. Normalize to pixels here so one notch means the
  // same zoom step everywhere — the engine caps a single event's contribution,
  // but a raw "3" would otherwise read as a dead scroll wheel.
  const WHEEL_PX = { 0: 1, 1: 16, 2: 400 };
  const onWheel = (e) => {
    e.preventDefault();
    const [x, y] = xy(e);
    app.wheel(e.deltaY * (WHEEL_PX[e.deltaMode] ?? 1), x, y);
  };

  canvas.addEventListener("mousedown", onDown);
  canvas.addEventListener("mousemove", onMove);
  canvas.addEventListener("mouseup", onUp);
  canvas.addEventListener("mouseout", onOut);
  canvas.addEventListener("wheel", onWheel, { passive: false });

  // --- keep sized to the container ---
  const ro = new ResizeObserver(() => applySize());
  ro.observe(box);
  const onResize = () => applySize();
  window.addEventListener("resize", onResize);

  const destroy = () => {
    if (destroyed) return;
    destroyed = true;
    if (rafId !== null) cancelAnimationFrame(rafId);
    ro.disconnect();
    window.removeEventListener("resize", onResize);
    window.removeEventListener("pagehide", destroy);
    canvas.removeEventListener("mousedown", onDown);
    canvas.removeEventListener("mousemove", onMove);
    canvas.removeEventListener("mouseup", onUp);
    canvas.removeEventListener("mouseout", onOut);
    canvas.removeEventListener("wheel", onWheel);
    app.free();
  };
  window.addEventListener("pagehide", destroy);

  // `kick` restarts the render loop — call it after an app method that starts an
  // animation (e.g. `set_highlight`, which tweens the camera to the matches) so
  // the tween actually runs; the loop stops itself once `animate` settles.
  return { destroy, app, kick: ensureFrame };
}
